import React, { useState } from 'react';
import { Mail, ArrowRight, CheckCircle, AlertCircle } from 'lucide-react';
import { FiXCircle } from 'react-icons/fi';

const WorkEmailCapture = ({ setShowPremium }) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');

  const validateWorkEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }

    // Common personal email domains to exclude
    const personalDomains = [
      'gmail.com',
      'yahoo.com',
      'hotmail.com',
      'outlook.com',
      'icloud.com',
      'aol.com',
    ];
    const domain = email.split('@')[1]?.toLowerCase();

    if (personalDomains.includes(domain)) {
      return 'Please use your work email address';
    }

    return null;
  };

  const handleSubmit = async () => {
    const validationError = validateWorkEmail(email);
    if (validationError) {
      setErrorMessage(validationError);
      setStatus('error');
      return;
    }

    setStatus('loading');
    setErrorMessage('');

    // Simulate API call
    try {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      setStatus('success');
    } catch (error) {
      setStatus('error');
      setErrorMessage('Something went wrong. Please try again.');
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (status === 'error') {
      setStatus('idle');
      setErrorMessage('');
    }
  };

  if (status === 'success') {
    return (
      <div className="relative mx-auto max-w-md rounded-2xl border border-zinc-800 bg-zinc-900 p-8 text-center shadow-xl">
        <div className="absolute right-4 top-4">
          <button
            onClick={() => setShowPremium(false)}
            className="rounded-full p-1 text-zinc-400 transition-colors duration-200 hover:text-white"
          >
            <FiXCircle size={18} />
          </button>
        </div>
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-black">
          <CheckCircle className="h-8 w-8 text-white" />
        </div>
        <h3 className="mb-2 text-xl font-semibold text-white">
          Access Granted!
        </h3>
        <p className="mb-6 text-zinc-400">
          We've sent you access instructions at{' '}
          <span className="font-medium text-white">{email}</span>
        </p>

        <button
          onClick={() => {
            setStatus('idle');
            setEmail('');
          }}
          className="mb-4 text-sm font-medium text-zinc-400 transition-colors hover:text-zinc-200"
        >
          Submit another email
        </button>
      </div>
    );
  }

  return (
    <div className="relative mx-auto max-w-md rounded-2xl border border-zinc-800 bg-zinc-900 p-8 shadow-xl">
      <div className="absolute right-4 top-4">
        <button
          onClick={() => setShowPremium(false)}
          className="rounded-full p-1 text-zinc-400 transition-colors duration-200 hover:text-white"
        >
          <FiXCircle size={18} />
        </button>
      </div>
      <div className="mb-8 text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-black">
          <Mail className="h-8 w-8 text-white" />
        </div>
        <h2 className="mb-2 text-2xl font-bold text-white">Get Early Access</h2>
        <p className="text-zinc-400">
          Enter your work email to unlock premium features
        </p>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <label
            htmlFor="work-email"
            className="block text-sm font-medium text-zinc-300"
          >
            Work Email Address
          </label>
          <div className="relative">
            <input
              id="work-email"
              type="email"
              value={email}
              onChange={handleEmailChange}
              onInput={handleEmailChange}
              placeholder="you@company.com"
              className={`w-full rounded-xl border bg-zinc-800 px-4 py-3 text-white placeholder-zinc-500 transition-all duration-200 focus:border-transparent focus:ring-2 focus:ring-white ${
                status === 'error'
                  ? 'border-red-500 bg-red-950'
                  : 'border-zinc-700 hover:border-zinc-600 focus:border-white'
              }`}
              disabled={status === 'loading'}
            />
            {status === 'error' && (
              <AlertCircle className="absolute right-3 top-3.5 h-5 w-5 text-red-400" />
            )}
          </div>
          {status === 'error' && (
            <p className="flex items-center gap-1 text-sm text-red-500">
              {errorMessage}
            </p>
          )}
        </div>

        <button
          onClick={handleSubmit}
          disabled={!email.trim() || status === 'loading'}
          className="group flex w-full items-center justify-center gap-2 rounded-xl bg-zinc-700 px-4 py-3 font-medium text-white transition-all duration-200 hover:bg-zinc-600 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {status === 'loading' ? (
            <>
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
              Processing...
            </>
          ) : (
            <>
              Get Access
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </>
          )}
        </button>
      </div>

      <div className="mt-6 text-center">
        <p className="text-xs text-zinc-500">
          We'll only use your email to grant feature access.{' '}
          <span className="font-medium text-zinc-300">No spam, ever.</span>
        </p>
      </div>
    </div>
  );
};

export default WorkEmailCapture;
